// Registry block dispatch — Phase 4 of the chrome redesign.
//
// Maps a PluginRegistryBlock { componentId, version, intent, props }
// to a concrete component from @openanalyst-ai/ui/registry.
//
// New components are added to REGISTRY_MAP below — the map is the single
// source of truth the host uses to dispatch. Component IDs are versioned
// (`atomic.button.v1`) so we can ship `v2` later without breaking
// existing plugin manifests pinned to `v1`.
//
// Plugin-side authoring example:
//
//   {
//     id: "summary",
//     type: "registry",
//     componentId: "layout.section",
//     version: "v1",
//     slots: {
//       title: "Summary",
//       children: [{ componentId: "display.kpi-stat", version: "v1", props: { label: "Revenue", value: "$12k" } }]
//     }
//   }
//
// See planning/plugin-platform-design.md.

import type { JSX } from "solid-js"
import type { PluginRegistryBlock } from "@openanalyst-ai/plugin"
import { registryEntry, registryKey, registryComponentExists } from "./registry-catalog"
import type { RegistryRuntime } from "./registry-submit-runtime"

export { registryComponentExists }
export type { RegistryRuntime } from "./registry-submit-runtime"

export interface RegistryBlockProps {
  block: PluginRegistryBlock
  runtime?: RegistryRuntime
}

type RegistryNode = {
  componentId: string
  version: string
  intent?: string
  props?: Record<string, unknown>
  slots?: Record<string, RegistrySlot>
}

type RegistrySlotItem = string | number | boolean | RegistryNode
type RegistrySlot = RegistrySlotItem | RegistrySlotItem[]

const MAX_DEPTH = 6
const MAX_NODES = 80
const MAX_SLOT_ITEMS = 50
const MAX_TEXT_LENGTH = 4000

function asRecord(value: unknown) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function isRegistryNode(value: unknown): value is RegistryNode {
  const record = asRecord(value)
  return typeof record.componentId === "string" && typeof record.version === "string"
}

function safeText(value: string | number | boolean, id: string) {
  const text = String(value)
  if (text.length <= MAX_TEXT_LENGTH) return text
  return <RegistryError kind="limit" id={id} message="Registry slot text is too long." />
}

function allowedProps(node: RegistryNode) {
  const entry = registryEntry(node.componentId, node.version)
  if (!entry) return {}
  const props = asRecord(node.props)
  return Object.fromEntries(
    entry.props
      .filter((key) => props[key] !== undefined)
      .map((key) => [key, props[key]]),
  )
}

function renderedSlots(node: RegistryNode, depth: number, metrics: { nodes: number }, blockId: string, runtime?: RegistryRuntime) {
  const entry = registryEntry(node.componentId, node.version)
  if (!entry) return {}
  const slots = asRecord(node.slots)
  return Object.fromEntries(
    entry.slots
      .filter((key) => slots[key] !== undefined)
      .map((key) => [key, renderSlot(slots[key] as RegistrySlot, depth + 1, metrics, blockId, runtime)]),
  )
}

function renderSlot(slot: RegistrySlot, depth: number, metrics: { nodes: number }, blockId: string, runtime?: RegistryRuntime): JSX.Element {
  if (Array.isArray(slot)) {
    if (slot.length > MAX_SLOT_ITEMS) {
      return <RegistryError kind="limit" id={blockId} message="Registry slot has too many items." />
    }
    return slot.map((item) => renderSlot(item, depth, metrics, blockId, runtime))
  }
  if (typeof slot === "string" || typeof slot === "number" || typeof slot === "boolean") return safeText(slot, blockId)
  if (isRegistryNode(slot)) return renderNode(slot, depth, metrics, blockId, runtime)
  return undefined
}

function renderNode(node: RegistryNode, depth: number, metrics: { nodes: number }, blockId: string, runtime?: RegistryRuntime): JSX.Element {
  const key = registryKey(node.componentId, node.version)
  const entry = registryEntry(node.componentId, node.version)
  metrics.nodes += 1
  if (depth > MAX_DEPTH) return <RegistryError kind="limit" id={blockId} message="Registry tree is too deep." />
  if (metrics.nodes > MAX_NODES) return <RegistryError kind="limit" id={blockId} message="Registry tree has too many nodes." />
  if (!entry?.authorable) {
    return <RegistryError kind="unsupported" id={key} message={`Registry component ${key} is not available for plugin UI.`} />
  }
  const Component = entry.component
  const props = {
    ...allowedProps(node),
    ...(node.intent && entry.props.includes("intent") ? { intent: node.intent } : {}),
    ...renderedSlots(node, depth, metrics, blockId, runtime),
    ...(depth === 1 && entry.runtime === "submit" && runtime ? { registryRuntime: runtime } : {}),
  }
  return (
    <div data-registry-component={node.componentId} data-registry-version={node.version}>
      <Component {...props} />
    </div>
  )
}

// Dispatches a PluginRegistryBlock to the right component. Falls back
// to a small fallback block when the componentId is unknown — that's
// almost always a versioning mismatch (plugin pinned a newer version
// than the host has shipped). The fallback explains the mismatch in
// dev mode and degrades to nothing in prod.
export function RegistryBlock(props: RegistryBlockProps) {
  return (
    <div
      data-registry-block-id={props.block.id}
      data-registry-component={props.block.componentId}
      data-registry-version={props.block.version}
    >
      {renderNode(props.block, 1, { nodes: 0 }, props.block.id, props.runtime)}
    </div>
  )
}

function RegistryError(props: { kind: "unsupported" | "limit"; id: string; message: string }) {
  return (
    <div
      role="status"
      data-registry-error={props.kind}
      class="rounded-lg border border-dashed border-border-weak-base bg-background-secondary px-3 py-3 text-12-regular text-text-weak"
    >
      {props.message} <code class="text-text-base">{props.id}</code>
    </div>
  )
}
